import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WinStateModal = ({ isOpen, onClose, duelTitle }) => {
  const [animationPhase, setAnimationPhase] = useState('enter');

  useEffect(() => {
    if (isOpen) {
      setAnimationPhase('enter');
      
      // Trigger celebration animation
      const timer1 = setTimeout(() => {
        setAnimationPhase('celebrate');
      }, 100);

      // Auto close after 4 seconds
      const timer2 = setTimeout(() => {
        handleClose();
      }, 4000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimationPhase('exit');
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: animationPhase === 'exit' ? 0 : 1 }}
        onClick={handleClose}
      />
      
      <div 
        className="relative w-full max-w-md bg-gradient-to-br from-primary/20 via-card to-success/20 rounded-2xl shadow-2xl overflow-hidden transition-all duration-500"
        style={{
          transform: animationPhase === 'celebrate' ? 'scale(1)' : 'scale(0.8)',
          opacity: animationPhase === 'exit' ? 0 : 1
        }}
      >
        {/* Confetti Background Effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)]?.map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random()}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative p-8 text-center space-y-6">
          {/* Victory Check Mark */}
          <div className="relative mx-auto w-32 h-32">
            {/* Pulsing Circle Background */}
            <div 
              className="absolute inset-0 bg-success/20 rounded-full animate-ping"
              style={{ animationDuration: '1.5s' }}
            />
            <div className="absolute inset-0 bg-success/30 rounded-full animate-pulse" />
            
            {/* Check Mark Circle */}
            <div 
              className="absolute inset-0 bg-gradient-to-br from-success to-success/80 rounded-full flex items-center justify-center shadow-lg transition-transform duration-700"
              style={{
                transform: animationPhase === 'celebrate' ? 'rotate(0deg) scale(1)' : 'rotate(-180deg) scale(0)'
              }}
            >
              <Icon 
                name="Check" 
                size={64} 
                className="text-white drop-shadow-lg"
                style={{
                  strokeWidth: 3
                }}
              />
            </div>
          </div>

          {/* Victory Text */}
          <div className="space-y-2">
            <h2 
              className="text-3xl font-bold text-foreground transition-all duration-500"
              style={{
                transform: animationPhase === 'celebrate' ? 'translateY(0)' : 'translateY(20px)',
                opacity: animationPhase === 'celebrate' ? 1 : 0
              }}
            >
              ¡Día Cumplido! 🎉
            </h2>
            <p 
              className="text-lg text-muted-foreground transition-all duration-500 delay-100"
              style={{
                transform: animationPhase === 'celebrate' ? 'translateY(0)' : 'translateY(20px)',
                opacity: animationPhase === 'celebrate' ? 1 : 0
              }}
            >
              Has completado tu objetivo de hoy
            </p>
            {duelTitle && (
              <p 
                className="text-sm font-medium text-primary transition-all duration-500 delay-200"
                style={{
                  transform: animationPhase === 'celebrate' ? 'translateY(0)' : 'translateY(20px)',
                  opacity: animationPhase === 'celebrate' ? 1 : 0
                }}
              >
                {duelTitle}
              </p>
            )}
          </div>

          {/* Stats */}
          <div 
            className="flex items-center justify-center space-x-6 transition-all duration-500 delay-300"
            style={{
              transform: animationPhase === 'celebrate' ? 'translateY(0)' : 'translateY(20px)',
              opacity: animationPhase === 'celebrate' ? 1 : 0
            }}
          >
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 text-success">
                <Icon name="CheckCircle2" size={20} />
                <span className="text-2xl font-bold">+1</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Día Completado</p>
            </div>
            <div className="w-px h-12 bg-border" />
            <div className="text-center">
              <div className="flex items-center justify-center space-x-1 text-primary">
                <Icon name="Trophy" size={20} />
                <span className="text-2xl font-bold">+10</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Puntos</p>
            </div>
          </div>

          {/* Close Button */}
          <Button
            onClick={handleClose}
            variant="outline"
            className="transition-all duration-500 delay-400"
            style={{
              transform: animationPhase === 'celebrate' ? 'translateY(0)' : 'translateY(20px)',
              opacity: animationPhase === 'celebrate' ? 1 : 0
            }}
          >
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default WinStateModal;